import { Mark, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        commentAnchor: {
            /** Wrap the current selection in a comment-anchor highlight tagged with `anchorId`. */
            setCommentAnchor: (attrs: { anchorId: string }) => ReturnType;
            /** Remove the anchor highlight whose id matches (used on resolve). */
            unsetCommentAnchorById: (anchorId: string) => ReturnType;
        };
    }
}

export interface CommentAnchorAttrs {
    anchorId: string | null;
}

/**
 * `comment-anchor` — an inline Mark that highlights the notes span an anchored comment points at, tagged
 * with the comment's anchor id. Round-trips through the saved notes HTML as
 * `<span data-comment-anchor data-comment-id="…" class="vf-anchor">`, so highlights survive reload; the
 * app layer re-finds by quoted text when the mark is gone (orphan handling — TASK_CONVERSATIONS_PLAN.md §7).
 * `any` on the command context matches the other extensions in this folder (Tiptap's loosely-typed chains).
 */
export const CommentAnchor = Mark.create({
    name: 'commentAnchor',
    // Don't grow the highlight when typing at its edge, and keep it a single logical span.
    inclusive: false,
    spanning: true,

    addAttributes() {
        return {
            anchorId: {
                default: null,
                parseHTML: (el: HTMLElement) => el.getAttribute('data-comment-id'),
                renderHTML: (attributes: any) =>
                    attributes.anchorId ? { 'data-comment-id': attributes.anchorId } : {}
            }
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-comment-anchor]' }];
    },

    renderHTML({ HTMLAttributes }: any) {
        return ['span', mergeAttributes(HTMLAttributes, { 'data-comment-anchor': '', class: 'vf-anchor' }), 0];
    },

    addCommands() {
        return {
            // Delegates through `commands`, NOT `chain().setMark(...).run()`: a command that opens its own
            // chain does not compose inside an outer one, so
            // `chain().setTextSelection(range).setCommentAnchor(id).run()` used to apply no mark at all —
            // the highlight on a passage being commented on never appeared.
            setCommentAnchor:
                (attrs: { anchorId: string }) =>
                    ({ commands }: any) =>
                        commands.setMark(this.name, attrs),

            unsetCommentAnchorById:
                (anchorId: string) =>
                    ({ tr, state, dispatch }: any) => {
                        const markType = state.schema.marks[this.name];
                        if (!markType) {
                            return false;
                        }
                        let changed = false;
                        state.doc.descendants((node: any, pos: number) => {
                            if (!node.isText) {
                                return;
                            }
                            const has = node.marks.some(
                                (m: any) => m.type === markType && m.attrs.anchorId === anchorId
                            );
                            if (has) {
                                tr.removeMark(pos, pos + node.nodeSize, markType);
                                changed = true;
                            }
                        });
                        if (changed && dispatch) {
                            dispatch(tr);
                        }
                        return changed;
                    }
        } as any;
    }
});
